import { useCallback, useEffect, useMemo, type ReactNode } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Columns3, Funnel, LayoutGrid, X, type LucideIcon } from 'lucide-react'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { useCoalescedRealtimeReload } from '@/features/realtime/hooks/useCoalescedRealtimeReload'
import { ProjectBlockedState } from '@/features/projects/components/ProjectBlockedState'
import { asApiError } from '@/services/apiError'
import { useActivityContext } from '@/services/websocket/useActivityContext'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'
import { boardViewSearch } from '../boardView'
import { BOARD_FOCUS_RING } from '../focusRing'
import { useBoardView } from '../hooks/useBoardView'
import {
  boardClosed,
  boardRefetchArmed,
  openBoard,
  selectBoardAllTasks,
  selectBoardBlocked,
  selectBoardEpics,
  selectBoardLoading,
  selectBoardNonce,
  selectBoardReadFailed,
  selectEpicTitle
} from '../slices/boardSlice'
import type { BoardView, EpicFilter } from '../types'
import { EpicsView } from './EpicsView'
import { TasksView } from './TasksView'

export interface BuildQueueProps {
  /** Omitted on `/project/:projectId/build` (the route param wins); the `/my/*` routes pass it. */
  projectId?: string
  /** `/my/*`: render only the caller's rows (assignee OR reviewer, `selectMyTasks` — Decision 2). */
  narrowToCaller?: boolean
  /**
   * `toggle` (the Build board): `[Epics | Tasks]` driven by `?view=` / `?epic=`.
   * `tasksOnly` (`/my/*`, Decision D1): the four-column Tasks view, no toggle,
   * no epic filter, and no view parameter is ever read or written.
   */
  mode?: 'toggle' | 'tasksOnly'
}

/**
 * The Build board container (PX-W7.4, TASK-41CE1DE2; reshaped by
 * CR-BUILD-BOARD-EPICS-2026-09-26). Owns the feature's mount load
 * (`openBoard` — tasks + backlog + epics — / `boardClosed`), the realtime
 * refetch, and the blocked / loading / error / empty ladder; it renders one
 * of two projections of that ONE load — `EpicsView` (the default) or
 * `TasksView` — from the URL (INV-R.3 / INV-R.4: toggling or filtering issues
 * no request). The toggle and the epic filter chip sit above the panel on
 * both views; `blocked` replaces the whole section.
 */
export function BuildQueue({
  projectId: projectIdProp,
  narrowToCaller = false,
  mode = 'toggle'
}: BuildQueueProps) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const { projectId: routeProjectId } = useParams<{ projectId: string }>()
  const projectId = projectIdProp ?? routeProjectId
  const all = useAppSelector(selectBoardAllTasks)
  const epics = useAppSelector(selectBoardEpics)
  const loading = useAppSelector(selectBoardLoading)
  const blocked = useAppSelector(selectBoardBlocked)
  // Finding 3 (fix wave 1, GRP-B3D9F416) + deferred finding c3-2: the failed
  // branch reads the COMBINED "did the primary read fail" selector, never the
  // shared `error` (verdict refusals, rendered by `BoardVerdictActions`) nor
  // the artefact/activity channels (rendered by `ReviewCard`/`RunLogPanel`) —
  // none of those may unmount the board. `blocked` is checked first so it
  // renders `ProjectBlockedState` (INV-4.5: the two are mutually exclusive,
  // so by the time this is read `readFailed` is exactly `listError`).
  const readFailed = useAppSelector(selectBoardReadFailed)
  const nonce = useAppSelector(selectBoardNonce)
  const { view, epicFilter } = useBoardView()
  const shownView: BoardView = mode === 'tasksOnly' ? 'tasks' : view

  // TASK-7922504E step C (Agon, 2026-09-23): hold this project's context on
  // the org's one activity socket. A client-side session is refused `org:`,
  // and the server tags every task event with `project:<id>` too, so this
  // hold is what keeps `/my/project` live for client users (internal users
  // also hold `org:`; the socket hands each frame over once either way).
  const liveContextIds = useMemo(
    () => (projectId === undefined ? [] : [`project:${projectId}`]),
    [projectId]
  )
  useActivityContext(liveContextIds)

  // Fix cycle 3 (GRP-B04710BD, px-effect-cleanup-on-nonce-dep-resets-the-slice):
  // the reset is its OWN effect, keyed only on identity (`orgId`/`projectId`),
  // so its cleanup fires on unmount and on a project change — never on a
  // realtime nonce bump. Folding it into the fetch effect below (which used
  // to carry `nonce` in the same dependency array) meant React ran this
  // cleanup on every nonce bump too, since React re-runs an effect's cleanup
  // whenever ANY of its own deps change, not only at unmount — wiping the
  // selected task and the whole queue on a remote realtime frame.
  useEffect(() => {
    return () => {
      dispatch(boardClosed())
    }
  }, [dispatch, orgId, projectId])

  // The load, keyed on identity only, no cleanup. `openBoard`'s own
  // `shouldFetch` condition dedupes StrictMode's double run and vetoes a
  // reload of a fresh cache.
  const loadBoard = useCallback(() => {
    if (orgId === null || projectId === undefined) return
    void dispatch(openBoard({ orgId, projectId }))
  }, [dispatch, orgId, projectId])

  // The mount load — immediate.
  useEffect(() => {
    loadBoard()
  }, [loadBoard])

  // The realtime reload, coalesced (fix cycle 1): the server's two frames per
  // status change cost ONE reload. The shared hook's cleanup only clears its
  // timer — the slice reset stays in the identity-keyed effect above.
  useCoalescedRealtimeReload(nonce, loadBoard)

  // Retry after a failed read: re-arm the cache FIRST — a failed load leaves
  // it un-invalidated, so a bare re-dispatch was vetoed by `shouldFetch` and
  // did nothing (memory `px-retry-needs-an-explicit-invalidate`).
  const onRetryClicked = useCallback(() => {
    if (orgId === null || projectId === undefined) return
    dispatch(boardRefetchArmed())
    void dispatch(openBoard({ orgId, projectId }))
  }, [dispatch, orgId, projectId])

  if (orgId === null || projectId === undefined) return null
  if (blocked !== null) {
    return <ProjectBlockedState blocked={{ kind: 'blocked', ...blocked }} />
  }

  // AC-16 / AC-22: the board is empty only when it has no task, no backlog row
  // AND no epic — an epic with zero tasks still earns its card.
  const empty = all.length === 0 && epics.length === 0
  const settled = !(loading && empty) && readFailed === null
  const body =
    loading && empty ? (
      <LoadingPanel label={t('board.queue.loading')} />
    ) : readFailed !== null ? (
      <ErrorPanel
        error={asApiError({ status: readFailed.status, key: readFailed.code, message: readFailed.message })}
        onRetryClicked={onRetryClicked}
      />
    ) : empty ? (
      <EmptyPanel title={t('board.queue.emptyTitle')} body={t('board.queue.emptyBody')} />
    ) : shownView === 'epics' ? (
      <EpicsView />
    ) : (
      <TasksView
        projectId={projectId}
        narrowToCaller={narrowToCaller}
        epicFilter={epicFilter}
        allowFilter={mode === 'toggle'}
      />
    )

  return (
    <section aria-label={t('board.view.region')} data-testid="build-queue">
      {mode === 'toggle' && (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <BoardViewToggle view={view} epicFilter={epicFilter} />
          {settled && view === 'tasks' && epicFilter.kind !== 'all' && <EpicFilterChip filter={epicFilter} />}
        </div>
      )}
      {body}
    </section>
  )
}

interface BoardViewToggleProps {
  view: BoardView
  epicFilter: EpicFilter
}

/**
 * `[ Epics | Tasks ]` — two `<Link>`s, not Radix Tabs: each is a URL write
 * (one pushed history entry, Cmd-click works), and Tabs would make it a
 * button-driven `navigate`. Two tab stops, Enter follows the link; the active
 * one carries `aria-current` (AC-17). The Tasks link keeps a current epic
 * filter, so activating the already-active option targets the current URL,
 * which the router applies as a replace — no new history entry.
 */
function BoardViewToggle({ view, epicFilter }: BoardViewToggleProps) {
  const { t } = useTranslation('projectWorkspace')
  const [params] = useSearchParams()
  const epicsTo = useMemo(() => ({ search: boardViewSearch(params, { view: 'epics' }) }), [params])
  const tasksTo = useMemo(
    () => ({ search: boardViewSearch(params, { view: 'tasks', epicFilter: view === 'tasks' ? epicFilter : undefined }) }),
    [params, view, epicFilter]
  )
  return (
    <nav aria-label={t('board.view.label')} className="inline-flex gap-0.5 rounded-lg border border-border bg-card p-0.5">
      <ViewLink to={epicsTo} active={view === 'epics'} testId="board-view-epics" icon={LayoutGrid}>
        {t('board.view.epics')}
      </ViewLink>
      <ViewLink to={tasksTo} active={view === 'tasks'} testId="board-view-tasks" icon={Columns3}>
        {t('board.view.tasks')}
      </ViewLink>
    </nav>
  )
}

interface ViewLinkProps {
  to: { search: string }
  active: boolean
  testId: string
  icon: LucideIcon
  children: ReactNode
}

function ViewLink({ to, active, testId, icon: Icon, children }: ViewLinkProps) {
  return (
    <Link
      to={to}
      data-testid={testId}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm',
        active ? 'bg-primary font-medium text-primary-foreground' : 'text-ink-soft hover:bg-muted hover:text-foreground',
        BOARD_FOCUS_RING
      )}
    >
      <Icon aria-hidden className="size-4" />
      <span>{children}</span>
    </Link>
  )
}

interface EpicFilterChipProps {
  filter: Exclude<EpicFilter, { kind: 'all' }>
}

/**
 * The visible epic filter above the Tasks view (AC-18): the epic's title,
 * "Unassigned", or — for an id no loaded epic carries — the raw id marked as
 * not on this board. Clear is a `<Link>` that drops `epic` (a pushed entry).
 */
function EpicFilterChip({ filter }: EpicFilterChipProps) {
  const { t } = useTranslation('projectWorkspace')
  const [params] = useSearchParams()
  const epicId = filter.kind === 'epic' ? filter.id : undefined
  const title = useAppSelector((s) => selectEpicTitle(s, epicId))
  const clearTo = useMemo(() => ({ search: boardViewSearch(params, { view: 'tasks' }) }), [params])
  const unknown = filter.kind === 'epic' && title === null
  const label = filter.kind === 'unassigned' ? t('board.epics.unassigned') : (title ?? filter.id)

  return (
    <div
      role="group"
      aria-label={t('board.filter.label')}
      data-testid="board-epic-filter"
      className="inline-flex max-w-full items-center gap-2 rounded-full border border-mint bg-accent py-0.5 pl-3 pr-1 text-[0.8125rem] text-accent-foreground"
    >
      <Funnel aria-hidden className="size-4 shrink-0" />
      <span className="min-w-0">
        {t('board.filter.prefix')} <b className="font-semibold [overflow-wrap:anywhere]">{label}</b>
        {unknown && <span data-testid="board-epic-filter-unknown"> · {t('board.filter.unknownEpic')}</span>}
      </span>
      {/* Fix cycle 1 (review low): the visible control is 1.5rem tall, under
          the 44 px touch target at 375. An invisible `after:` layer extends
          the HIT area 0.625rem above and below (1.5 + 1.25 = 2.75rem = 44 px)
          without changing what is drawn; it stays inside the toolbar's
          row gap, so it never covers the toggle above it. */}
      <Link
        to={clearTo}
        data-testid="board-epic-filter-clear"
        className={cn(
          "relative inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 font-medium after:absolute after:-inset-x-1 after:-inset-y-2.5 after:content-[''] hover:bg-card",
          BOARD_FOCUS_RING
        )}
      >
        <X aria-hidden className="size-4" />
        {t('board.filter.clear')}
      </Link>
    </div>
  )
}
